import React, { useContext, useEffect, useState, useCallback, useRef } from 'react';
import CanvasContext from '../../../context/CanvasContext';
import socket from './../../../socket'
import styles from './styles/ClassroomCanvasSidebar.module.css';
import { FaCircle, FaUserSlash, FaFile, FaMousePointer, FaCheckSquare, FaKeyboard, FaLock, FaUnlock } from 'react-icons/fa';

const ClassroomCanvasSidebar = ({ assignmentId, activeStudents, selectedUser, selectedUserPropFunction, lockedUsers, setLockedUsers }) => {
    const {
        selectedNodeId,
        assignmentWithSubmissions,
        highlightSubmission,
        nodes,
        edges,
        resetHighlightSubmission,
    } = useContext(CanvasContext);
    
    const [selectedNodeWork, setSelectedNodeWork] = useState({ workData: [], choiceLabelMap: new Map() });
    const [currentSelectedStudent, setCurrentSelectedStudent] = useState(null);
    const [selectedStudentSubmissions, setSelectedStudentSubmissions] = useState([]);

    useEffect(() => {
        const processNodeWork = () => {
            if (!selectedNodeId || !nodes || !edges || !assignmentWithSubmissions?.submissions) {
                setSelectedNodeWork({ workData: [], choiceLabelMap: new Map() });
                return;
            }

            const relevantChoices = edges.filter(edge => edge.source === selectedNodeId);
            const relevantChoiceIds = new Set(relevantChoices.map(edge => edge.id));
            const choiceLabelMap = new Map(relevantChoices.map(edge => [edge.id, edge.data?.title || '']));

            const workByViewer = new Map();

            for (const submission of assignmentWithSubmissions.submissions) {
                console.log("submission: ", submission);
                const viewerId = submission.viewer._id;
                const relevantDecisions = submission.decisions.filter(decision => relevantChoiceIds.has(decision._id) && decision.priority);

                if (relevantDecisions.length > 0) {
                    const attemptGroup = {
                        submissionId: submission._id,
                        timestamp: submission.updatedAt,
                        decisions: relevantDecisions.map((decision, index) => ({
                            ...decision,
                            _uniqueKey: `${submission._id}-${decision._id}-${index}`
                        }))
                    };

                    if (!workByViewer.has(viewerId)) {
                        workByViewer.set(viewerId, {
                            viewer: submission.viewer,
                            attempts: []
                        });
                    }

                    workByViewer.get(viewerId).attempts.push(attemptGroup);
                }
            }
            
            setSelectedNodeWork({
                workData: Array.from(workByViewer.values()),
                choiceLabelMap: choiceLabelMap
            });
        };

        processNodeWork();
    }, [selectedNodeId, assignmentWithSubmissions, nodes, edges]);

    
    const viewSubmissionWork = useCallback((student, selectedWhileActive) => {
        setCurrentSelectedStudent({student, selectedWhileActive});
        selectedUserPropFunction(student);

        const dataSource = assignmentWithSubmissions?.submissions;
        if (!dataSource || !student?.userId) return;

        const studentSubmissions = dataSource.filter(log => log.viewer._id === student.userId);


        if (studentSubmissions.length > 0) {
            const latestSubmissions = studentSubmissions.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
            
            setSelectedStudentSubmissions(latestSubmissions);
            
            if(student.startStatus){
                return;
            }
            
            highlightSubmission(latestSubmissions[0]);
        }
    }, [assignmentWithSubmissions, selectedStudentSubmissions, highlightSubmission]);

    const prevStudentSubmissionsRef = useRef('');

    useEffect(() => {
        if (!currentSelectedStudent || !assignmentWithSubmissions?.submissions) return;
        
        const searchActiveStudent = activeStudents.some(user => user.userId === currentSelectedStudent.student.userId);
        
        if(!searchActiveStudent && currentSelectedStudent.selectedWhileActive){
            resetHighlightSubmission();
            setCurrentSelectedStudent(null);
            selectedUserPropFunction(null);

            return;
        }

        const studentId = currentSelectedStudent.student.userId;
        const studentSubmissions = assignmentWithSubmissions.submissions.filter(
            (log) => log.viewer._id === studentId
        );

        const serialized = JSON.stringify(studentSubmissions);

        if (prevStudentSubmissionsRef.current !== serialized) {
            prevStudentSubmissionsRef.current = serialized;
            viewSubmissionWork(currentSelectedStudent.student, currentSelectedStudent.selectedWhileActive);
        }
    }, [assignmentWithSubmissions, currentSelectedStudent, activeStudents, viewSubmissionWork]);

    const getDecisionClass = (decision) => {
        const classes = [styles.decisionItem];
        if (decision.type === 'choice' || decision.type === 'input') {
            if (decision.correctness === 'correct') {
                classes.push(styles.correct);
            } else if (decision.correctness === 'incorrect') {
                classes.push(styles.incorrect);
            }
        }
        return classes.join(' ');
    };

    const handleLockedUsers = (uId, lockedBool) => {
        if(lockedBool){
            setLockedUsers(prev => [...prev, uId]);
        }
        else{
            setLockedUsers(prev => (prev.filter(id => id !== uId)));
        }
        // assignmentId lets the server persist the lock in Redis so it survives
        // teacher refreshes and student reconnects.
        socket.emit('lockUserUpdate', { uId, lockedBool, assignmentId });
    }

    useEffect(() => {
        if(!selectedUser) setCurrentSelectedStudent(null);
    }, [selectedUser]);

    const resetSelection = () => {
        setCurrentSelectedStudent(null);
        selectedUserPropFunction(null);
        resetHighlightSubmission();
    }

    return (
        <div className={styles.sidebarContainer}>
            <div className={styles.section}>
                <div className={styles.sectionHeader}>
                    <FaCircle className={styles.statusIconActive} />
                    <h4>Active Students ({activeStudents.length})</h4>
                </div>
                {activeStudents.length === 0 ? (
                    <div className={styles.emptyState}>
                        <FaUserSlash className={styles.emptyStateIcon} />
                        <h4>No Active Students</h4>
                        <p>No students are viewing the assignment.</p>
                    </div>
                ) : (
                    // This is our new scrollable container
                    <div className={`${styles.studentList} ${styles.scrollableList}`}>
                        {activeStudents.map((student, index) => (
                            // 1. Use a DIV as the main container for each item
                            <div
                                key={student.userId}
                                className={`${styles.studentItem} ${currentSelectedStudent?.student?.userId === student.userId ? styles.studentItemSelected : ''}`}
                            >
                                <button
                                    className={styles.studentInfoButton}
                                    onClick={() => {
                                        if(currentSelectedStudent && currentSelectedStudent.student.userId === student.userId) resetSelection();
                                        else viewSubmissionWork(student, true)
                                    }}
                                >
                                    <span className={styles.studentIndex}>{index + 1}</span>
                                    <div className={styles.studentInfo}>
                                        <span className={styles.studentName}>{student.userName}</span>
                                        <span className={styles.studentEmail}>{student.userEmail}</span>
    
                                        {student.startStatus && <span className={styles.studentStatus}>Inactive</span>}
                                    </div>
                                </button>
                                <button
                                    className={`${styles.lockButton} ${lockedUsers.includes(student.userId) ? styles.locked : ''}`}
                                    onClick={() => handleLockedUsers(student.userId, !lockedUsers.includes(student.userId))}
                                    aria-label={lockedUsers.includes(student.userId) ? 'Unlock Student' : 'Lock Student'}
                                >
                                    {lockedUsers.includes(student.userId) ? <FaLock /> : <FaUnlock />}
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className={styles.section}>
                <div className={styles.sectionHeader}>
                    <h4>All Students ({assignmentWithSubmissions?.students?.length || 0})</h4>
                </div>
                {assignmentWithSubmissions?.students ? (
                    <div className={`${styles.studentList} ${styles.scrollableList}`}>
                        {assignmentWithSubmissions.students.map((student, index) => {
                            const isActive = activeStudents.some(stud => stud.userId === student._id);
                            const studentData = isActive 
                                ? activeStudents.find((stud) => stud.userId === student._id)
                                : { userId: student._id, userName: student.username, userEmail: student.userEmail, startStatus: true };

                            return (
                                <div
                                    key={student._id}
                                    className={`${styles.studentItem} ${currentSelectedStudent?.student?.userId === student._id ? styles.studentItemSelected : ''}`}
                                >
                                    <button className={styles.studentInfoButton} onClick={() => {
                                        if(currentSelectedStudent && currentSelectedStudent.student.userId === studentData.userId) resetSelection();
                                        else viewSubmissionWork(studentData, false)
                                        }}>
                                        <span className={styles.studentIndex}>{index + 1}</span>
                                        <div className={styles.studentInfo}>
                                            <span className={styles.studentName}>{student.username}</span>
                                            <span className={styles.studentEmail}>{student.userEmail}</span>
                                        </div>
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <div className={styles.emptyState}>Loading...</div>
                )}
            </div>
            
            <div className={styles.section}>
                <div className={styles.sectionHeader}>
                    <h4>Submission Details</h4>
                </div>
                {!selectedNodeId ? (
                    <div className={styles.promptBox}>
                        <FaMousePointer className={styles.promptIcon} />
                        <h4>Select a Node</h4>
                        <p>Click a node on the canvas to view submissions.</p>
                    </div>
                ) : selectedNodeWork?.workData?.length > 0 ? (
                    <div className={styles.workDisplayContainer}>
                        {selectedNodeWork.workData.map((viewerWork) => (
                            <div key={viewerWork.viewer._id} className={styles.workCard}>
                                <h5 className={styles.workCardHeader}>{viewerWork.viewer.username}'s Response</h5>
                                {viewerWork.attempts.map((attempt, attemptIndex) => (
                                    <div key={`${attempt.submissionId}-${attemptIndex}`} className={styles.attemptGroup}>
                                        <span className={styles.attemptTimestamp}>
                                            Attempt on {new Date(attempt.timestamp).toLocaleString()}
                                        </span>
                                        <ul className={styles.decisionList}>
                                            {attempt.decisions.map((decisionInfo) => {
                                                const decisionContent = {
                                                    icon: null,
                                                    text: null
                                                };

                                                if (decisionInfo.type === 'choice') {
                                                    decisionContent.icon = <FaCheckSquare className={styles.decisionIcon} />;
                                                    decisionContent.text = (
                                                        <>
                                                            <span className={styles.decisionLabel}>Chose:</span>
                                                            {selectedNodeWork.choiceLabelMap.get(decisionInfo.id) || 'Unknown Choice'}
                                                        </>
                                                    );
                                                } else if (decisionInfo.type === 'input') {
                                                    decisionContent.icon = <FaKeyboard className={styles.decisionIcon} />;
                                                    decisionContent.text = (
                                                        <>
                                                            <span className={styles.decisionLabel}>Input:</span>
                                                            "{decisionInfo.userInput}"
                                                        </>
                                                    );
                                                }

                                                if (!decisionContent.text) return null;

                                                return (
                                                    <li key={decisionInfo._uniqueKey} className={getDecisionClass(decisionInfo)}>
                                                        {decisionContent.icon}
                                                        <span className={styles.decisionTextContainer}>{decisionContent.text}</span>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className={styles.emptyState}>
                         <FaFile className={styles.emptyStateIcon} />
                        <h4>No Submission</h4>
                        <p>No work has been submitted for this node yet.</p>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ClassroomCanvasSidebar;